import React, { useState } from "react";
import { ChevronDown, ChevronRight, Check, Loader2, AlertCircle, Terminal } from "lucide-react";

interface ToolCallIndicatorProps {
  toolName: string;
  toolCallId: string;
  state: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

export function ToolCallIndicator({ toolName, state, input, output, errorText }: ToolCallIndicatorProps) {
  const [expanded, setExpanded] = useState(false);

  const isRunning = state === "input-streaming" || state === "input-available";
  const isError = state === "output-error";
  const isDone = state === "output-available";

  const StatusIcon = isRunning
    ? () => <Loader2 size={12} className="animate-spin text-accent-violet" />
    : isError
      ? () => <AlertCircle size={12} className="text-ca-punch" />
      : () => <Check size={12} className="text-accent-emerald" />;

  const statusLabel = isRunning ? "Running" : isError ? "Failed" : "Done";

  return (
    <div className="cai-glass rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-raised transition-colors"
      >
        <Terminal size={11} className="text-accent-violet shrink-0" />
        <span className="text-[11px] text-text-primary font-medium truncate flex-1">
          {toolName}
        </span>
        <StatusIcon />
        <span className="text-[10px] text-text-secondary">{statusLabel}</span>
        {expanded ? (
          <ChevronDown size={11} className="text-text-secondary shrink-0" />
        ) : (
          <ChevronRight size={11} className="text-text-secondary shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border-subtle px-3 py-2.5 space-y-2.5">
          {input != null && (
            <div>
              <div className="text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1">
                Input
              </div>
              <pre className="p-2.5 bg-surface-glass text-text-secondary rounded-lg text-[10px] font-mono max-h-40 overflow-auto cai-scrollbar whitespace-pre-wrap leading-relaxed border border-border-subtle">
                {typeof input === "string" ? input : JSON.stringify(input, null, 2)}
              </pre>
            </div>
          )}

          {(isDone && output != null) && (
            <div>
              <div className="text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1">
                Output
              </div>
              <pre className="p-2.5 bg-surface-glass text-text-secondary rounded-lg text-[10px] font-mono max-h-40 overflow-auto cai-scrollbar whitespace-pre-wrap leading-relaxed border border-border-subtle">
                {typeof output === "string" ? output : JSON.stringify(output, null, 2)}
              </pre>
            </div>
          )}

          {isError && errorText && (
            <div>
              <div className="text-[9px] font-semibold text-text-muted uppercase tracking-wider mb-1">
                Error
              </div>
              <pre className="p-2.5 bg-ca-punch/8 text-ca-punch rounded-lg text-[10px] font-mono max-h-40 overflow-auto cai-scrollbar whitespace-pre-wrap leading-relaxed border border-ca-punch/15">
                {errorText}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
